// pages/legal/TermsPage.jsx
// Route: /terms
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'July 29, 2026';
const COMPANY        = 'Arhat Info Corp';
const SITE           = 'arhat.info';
const EMAIL          = 'legal@arhat.info';
const LOCATION       = 'Surrey, British Columbia, Canada';

const SECTIONS = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: `By accessing or using ${SITE} (the "Site") and any services, tools, calculators, or content available on or through the Site (collectively, the "Services"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, do not use the Services.

These Terms apply to all visitors, registered users, and paying subscribers. ${COMPANY} ("we", "us", "our") reserves the right to update these Terms at any time. Continued use of the Services after changes are posted constitutes acceptance of the revised Terms. The effective date at the top of this page indicates when the Terms were last updated.`,
  },
  {
    id: 'description',
    title: '2. Description of Services',
    content: `${SITE} provides a suite of web-based tools and resources including:

• Free utility tools: network tools (IP lookup, DNS checker, port scanner), developer utilities (JSON formatter, JWT decoder, UUID generator, hash generator), text tools, file conversion tools, and business calculators.

• Financial calculators: FIRE calculators, mortgage calculators, compound interest calculators, RRSP/TFSA calculators, and other financial planning tools. These are provided for informational and educational purposes only and do not constitute financial, tax, investment, or legal advice.

• AI-powered portals (paid subscription): AI Job Search Copilot, Freelancer AI Toolkit, and Developer AI Tools, powered by OpenAI's GPT-4o model.

• Blog content covering personal finance, investing, developer tools, and freelancing topics.

• Chrome browser extensions that extend the functionality of the above portals.

We reserve the right to modify, suspend, or discontinue any part of the Services at any time with or without notice.`,
  },
  {
    id: 'accounts',
    title: '3. User Accounts',
    content: `3.1 Registration. Some features require you to create an account. You agree to provide accurate, current, and complete information and to update it as needed to keep it accurate.

3.2 Account Security. You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. Notify us immediately at ${EMAIL} if you suspect unauthorized access.

3.3 Age Requirement. You must be at least 16 years old to create an account. If you are under 18, you confirm that you have parental or guardian consent to use the Services.

3.4 One Account Per Person. You may not create multiple accounts to circumvent subscription limits, free trials, or any restrictions.

3.5 Account Termination. We reserve the right to suspend or terminate your account at our sole discretion if we believe you have violated these Terms, engaged in fraudulent activity, or misused the Services.`,
  },
  {
    id: 'subscriptions',
    title: '4. Subscriptions and Billing',
    content: `4.1 Subscription Plans. Access to AI-powered portals requires a paid subscription. Subscription tiers, features, and pricing are described on the /pricing page and may be updated from time to time.

4.2 Free Trial. We may offer a free trial period. Only one free trial is permitted per person or household. At the end of the trial, your subscription will automatically convert to a paid plan at the stated price unless you cancel before the trial ends.

4.3 Billing. Subscriptions are billed in advance on a monthly or annual basis. All payments are processed securely by Stripe. By providing payment information, you authorize us to charge you on the applicable billing cycle.

4.4 Automatic Renewal. Subscriptions renew automatically unless cancelled. You can cancel at any time through your account settings or by contacting ${EMAIL}. Cancellation takes effect at the end of the current billing period — you retain access until then.

4.5 Refunds. We offer a 7-day refund policy for new subscriptions. If you are not satisfied within 7 days of your first payment, contact ${EMAIL} for a full refund. No refunds are issued after 7 days or for partial billing periods. Refunds are issued to the original payment method.

4.6 Price Changes. We will give at least 30 days notice of any price increases, delivered via email or in-app notification. Continued use after the effective date of a price change constitutes acceptance.

4.7 Taxes. Prices shown may not include applicable taxes (GST/HST for Canadian customers, VAT for EU customers). Applicable taxes will be added at checkout based on your billing address.`,
  },
  {
    id: 'acceptable-use',
    title: '5. Acceptable Use',
    content: `You agree not to use the Services to:

• Violate any applicable law, regulation, or third-party rights.
• Scrape, crawl, or systematically extract data from the Site without written permission.
• Attempt to bypass, disable, or circumvent any security feature, access control, or subscription paywall.
• Use the AI tools to generate content that is defamatory, harassing, threatening, obscene, or illegal.
• Use the AI tools to generate spam, phishing content, misinformation, or content designed to deceive.
• Reverse engineer, decompile, or attempt to extract the source code of the Services.
• Use automated tools, bots, or scripts to interact with the Services in ways that place excessive load on our infrastructure.
• Resell, sublicense, or redistribute access to paid features without written permission.
• Impersonate another person or entity.
• Interfere with or disrupt the integrity or performance of the Services.

Violation of these terms may result in immediate account termination and, where applicable, legal action.`,
  },
  {
    id: 'ai-tools',
    title: '6. AI-Generated Content',
    content: `6.1 No Guarantee of Accuracy. Our AI tools generate content using large language models (OpenAI GPT-4o). AI-generated output may contain errors, inaccuracies, or outdated information. We do not guarantee the accuracy, completeness, or suitability of any AI-generated content.

6.2 Your Responsibility. You are solely responsible for reviewing, editing, and verifying any AI-generated content before using it. Do not submit confidential, sensitive, or personally identifiable information of third parties into the AI tools.

6.3 Not Professional Advice. AI-generated cover letters, resume summaries, recruiter replies, job match analyses, code, SQL queries, and similar outputs are tools to assist you — they are not professional legal, financial, medical, or career advice.

6.4 Content Ownership. You retain ownership of the inputs you provide. AI-generated outputs produced from your inputs are assigned to you, subject to OpenAI's usage policies. You grant us a limited license to process your inputs to deliver the Services.

6.5 Third-Party AI Provider. Our AI features are powered by OpenAI. By using AI features, you also agree to comply with OpenAI's usage policies at openai.com/policies/usage-policies.`,
  },
  {
    id: 'financial-tools',
    title: '7. Financial Calculators — Disclaimer',
    content: `7.1 Educational Purpose Only. All financial calculators, projections, and tools on ${SITE} — including FIRE calculators, RRSP/TFSA calculators, mortgage calculators, rental property calculators, and all others — are provided for educational and informational purposes only.

7.2 Not Financial Advice. Nothing on this Site constitutes financial, investment, tax, or legal advice. The results produced by our calculators are estimates based on the inputs you provide and standard mathematical formulas. They do not account for your individual circumstances, risk tolerance, tax situation, or applicable regulations.

7.3 No Reliance. Do not make any financial decision solely based on the output of our calculators. Always consult a qualified financial advisor, accountant, or lawyer before making significant financial decisions.

7.4 Canadian Context. Some tools are specifically designed for the Canadian market (RRSP, TFSA, CPP, GST/HST). While we strive to keep information current, tax laws and contribution limits change annually. Always verify current limits with the Canada Revenue Agency (CRA) at canada.ca/cra.

7.5 Accuracy Not Guaranteed. We make no representations that the calculations are free of errors. Market returns, tax rates, and other assumptions used in our tools are illustrative only.`,
  },
  {
    id: 'intellectual-property',
    title: '8. Intellectual Property',
    content: `8.1 Our IP. All content on ${SITE} — including but not limited to text, graphics, logos, icons, tool designs, code, blog articles, and the selection and arrangement of content — is the property of ${COMPANY} or its licensors and is protected by Canadian and international copyright, trademark, and other intellectual property laws.

8.2 Limited License. We grant you a limited, non-exclusive, non-transferable, revocable license to use the Services for your personal or internal business purposes in accordance with these Terms.

8.3 No Redistribution. You may not copy, reproduce, republish, upload, post, transmit, or distribute any content from the Site without our prior written permission, except as expressly permitted by these Terms.

8.4 Blog Content. Blog articles may be shared with attribution to ${SITE} and a link to the original article. Reproducing entire articles verbatim on other websites is not permitted.

8.5 Feedback. If you provide feedback or suggestions about the Services, you grant us a perpetual, irrevocable, royalty-free license to use that feedback for any purpose.`,
  },
  {
    id: 'privacy',
    title: '9. Privacy',
    content: `Your use of the Services is governed by our Privacy Policy at ${SITE}/privacy, which is incorporated into these Terms by reference. By using the Services, you consent to the data practices described in our Privacy Policy.

Key points:
• We collect information you provide (email, name) and usage data.
• We do not sell your personal data to third parties.
• Financial calculator inputs are processed locally in your browser and are not stored on our servers.
• AI tool inputs are sent to OpenAI for processing. We do not permanently store your AI inputs after the session.
• We use cookies for authentication, preferences, and analytics.
• Canadian users have rights under PIPEDA (Personal Information Protection and Electronic Documents Act).`,
  },
  {
    id: 'third-party',
    title: '10. Third-Party Services',
    content: `The Services integrate with or link to third-party services including:

• OpenAI (AI processing) — openai.com/privacy
• Stripe (payment processing) — stripe.com/privacy
• Google (OAuth sign-in, analytics) — policies.google.com/privacy
• Microsoft (OAuth sign-in) — privacy.microsoft.com

We are not responsible for the practices or content of third-party services. Your use of third-party services is governed by their respective terms and privacy policies.

Links to third-party websites are provided for convenience only and do not constitute an endorsement. We are not responsible for the content, accuracy, or availability of external sites.`,
  },
  {
    id: 'disclaimers',
    title: '11. Disclaimers and Limitation of Liability',
    content: `11.1 "As Is" Service. The Services are provided "as is" and "as available" without warranties of any kind, either express or implied. We disclaim all warranties including merchantability, fitness for a particular purpose, and non-infringement.

11.2 Uptime. We do not guarantee that the Services will be uninterrupted, error-free, or available at any particular time. We may take the Services offline for maintenance at any time.

11.3 Limitation of Liability. To the maximum extent permitted by applicable law, ${COMPANY} shall not be liable for any indirect, incidental, special, consequential, or punitive damages — including lost profits, lost data, or loss of goodwill — arising out of or in connection with your use of the Services, even if we have been advised of the possibility of such damages.

11.4 Maximum Liability. Our total liability to you for any claims arising from your use of the Services shall not exceed the amount you paid us in the 12 months preceding the claim, or $100 CAD if you have not made any payments.

11.5 Indemnification. You agree to indemnify, defend, and hold harmless ${COMPANY} and its officers, directors, employees, and agents from any claims, damages, losses, or costs (including legal fees) arising from your use of the Services, your violation of these Terms, or your violation of any third-party rights.`,
  },
  {
    id: 'governing-law',
    title: '12. Governing Law and Disputes',
    content: `12.1 Governing Law. These Terms are governed by the laws of the Province of British Columbia and the federal laws of Canada applicable therein, without regard to conflict of law principles.

12.2 Dispute Resolution. In the event of any dispute arising out of or relating to these Terms or the Services, the parties agree to first attempt to resolve the dispute informally by contacting ${EMAIL}. If the dispute is not resolved within 30 days, it shall be submitted to the exclusive jurisdiction of the courts located in Surrey, British Columbia, Canada.

12.3 Class Action Waiver. To the extent permitted by law, you waive any right to participate in a class action lawsuit or class-wide arbitration against ${COMPANY}.

12.4 Limitation Period. Any claim arising from your use of the Services must be brought within one (1) year of the event giving rise to the claim.`,
  },
  {
    id: 'chrome-extensions',
    title: '13. Chrome Extensions',
    content: `Our Chrome browser extensions ("Extensions") are subject to these Terms in addition to the Chrome Web Store Developer Program Policies.

• Extensions require a valid paid subscription to access AI features.
• Extensions access text on web pages only when you explicitly activate them (click the sidebar button or use right-click context menu). We do not read or collect the content of pages you visit.
• Extensions store authentication tokens locally in chrome.storage.local. These are never transmitted to third parties other than our own API.
• You may uninstall Extensions at any time through the Chrome extensions manager.
• We may update Extensions automatically through the Chrome Web Store. Continued use after an update constitutes acceptance of any changes.`,
  },
  {
    id: 'termination',
    title: '14. Termination',
    content: `14.1 By You. You may stop using the Services and close your account at any time by visiting your account settings or contacting ${EMAIL}. Cancellation of a subscription does not entitle you to a refund unless you are within the 7-day refund window.

14.2 By Us. We may suspend or terminate your access to the Services immediately and without notice if you violate these Terms, engage in fraudulent activity, or if we cease to operate the Services.

14.3 Effect of Termination. Upon termination, your right to use the Services ends immediately. Sections that by their nature should survive termination (including intellectual property, disclaimers, limitation of liability, and governing law) will survive.

14.4 Data Deletion. Upon account deletion, we will delete your personal data within 30 days, except where we are required by law to retain it or where it has been anonymized for analytics purposes.`,
  },
  {
    id: 'general',
    title: '15. General Provisions',
    content: `15.1 Entire Agreement. These Terms, together with our Privacy Policy and any additional terms applicable to specific features, constitute the entire agreement between you and ${COMPANY} regarding the Services.

15.2 Severability. If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.

15.3 Waiver. Our failure to enforce any right or provision of these Terms shall not constitute a waiver of that right or provision.

15.4 Assignment. You may not assign your rights or obligations under these Terms without our prior written consent. We may assign our rights and obligations without restriction.

15.5 Notices. We may send notices to you by email to the address associated with your account or by posting notices on the Site. Notices to us should be sent to ${EMAIL}.

15.6 Language. These Terms are written in English. Any translation is provided for convenience only. In the event of a conflict, the English version prevails.`,
  },
  {
    id: 'contact',
    title: '16. Contact',
    content: `If you have any questions about these Terms, please contact us:

${COMPANY}
Surrey, British Columbia, Canada
Email: ${EMAIL}
Website: ${SITE}

For privacy-related inquiries: privacy@arhat.info
For billing inquiries: billing@arhat.info
For legal notices: legal@arhat.info`,
  },
];

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState(null);

  return (
    <div style={{
      background: '#07080c', minHeight: '100vh',
      fontFamily: '"DM Sans", system-ui, sans-serif',
      color: '#dce6fa',
    }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px 80px' }}>

        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <Link to="/" style={{ fontSize: 13, color: 'rgba(220,230,250,.4)', textDecoration: 'none' }}>
            ← Tylented
          </Link>
          <h1 style={{
            fontSize: 'clamp(1.8rem,4vw,2.8rem)', fontWeight: 900,
            letterSpacing: '-.03em', margin: '16px 0 8px',
          }}>
            Terms and Conditions
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(220,230,250,.45)', lineHeight: 1.6 }}>
            Effective date: <strong style={{ color: 'rgba(220,230,250,.7)' }}>{EFFECTIVE_DATE}</strong>
            {' · '}Last updated: <strong style={{ color: 'rgba(220,230,250,.7)' }}>{EFFECTIVE_DATE}</strong>
          </p>
          <p style={{ fontSize: 14, color: 'rgba(220,230,250,.5)', lineHeight: 1.7, marginTop: 12, maxWidth: 680 }}>
            Please read these Terms carefully before using {SITE}. These Terms form a legally binding agreement between you and {COMPANY}.
          </p>
        </div>

        {/* Table of contents */}
        <div style={{
          padding: '20px 24px', borderRadius: 12,
          background: 'rgba(255,255,255,.03)',
          border: '1px solid rgba(255,255,255,.07)',
          marginBottom: 40,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(220,230,250,.35)', marginBottom: 14 }}>
            Contents
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '6px 24px' }}>
            {SECTIONS.map(s => (
              <a
                key={s.id}
                href={`#${s.id}`}
                style={{ fontSize: 13, color: 'rgba(220,230,250,.55)', textDecoration: 'none', lineHeight: 1.8 }}
                onMouseEnter={e => e.target.style.color = '#00c8f0'}
                onMouseLeave={e => e.target.style.color = 'rgba(220,230,250,.55)'}
              >
                {s.title}
              </a>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {SECTIONS.map((section, idx) => (
            <div
              key={section.id}
              id={section.id}
              style={{
                borderTop: '1px solid rgba(255,255,255,.06)',
                padding: '28px 0',
              }}
            >
              <h2 style={{
                fontSize: 17, fontWeight: 800, color: '#dce6fa',
                letterSpacing: '-.01em', marginBottom: 14,
              }}>
                {section.title}
              </h2>
              <div style={{
                fontSize: 14, color: 'rgba(220,230,250,.65)',
                lineHeight: 1.85, whiteSpace: 'pre-line',
              }}>
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 48, padding: '20px 24px', borderRadius: 12,
          background: 'rgba(0,200,240,.05)',
          border: '1px solid rgba(0,200,240,.15)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#00c8f0', marginBottom: 8 }}>
            Questions about these Terms?
          </div>
          <div style={{ fontSize: 13, color: 'rgba(220,230,250,.55)', lineHeight: 1.7 }}>
            Email us at{' '}
            <a href={`mailto:${EMAIL}`} style={{ color: '#00c8f0', textDecoration: 'none' }}>{EMAIL}</a>
            {' '}and we'll respond within 2 business days.
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Link to="/privacy" style={{ fontSize: 13, color: 'rgba(220,230,250,.4)', textDecoration: 'none' }}>
              Privacy Policy →
            </Link>
            <Link to="/pricing" style={{ fontSize: 13, color: 'rgba(220,230,250,.4)', textDecoration: 'none' }}>
              Pricing →
            </Link>
            <Link to="/" style={{ fontSize: 13, color: 'rgba(220,230,250,.4)', textDecoration: 'none' }}>
              Home →
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
